import { Component, inject, OnInit } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { NavigationEnd, Router, RouterLink, RouterOutlet } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CallidusCurrencyPipe } from '@shared/pipes/currency/callidus-currency.pipe';
import { unsubscribeMixin } from '@shared/mixin/unsubscribe.mixin';
import { take, takeUntil } from 'rxjs';
import { FormsModule } from '@angular/forms';
import { AnswerOption, QuestionBoxComponent } from '@shared/components/question-box/question-box.component';
import { AppLanguage } from '@shared/models/app-language.model';
import { LanguagePickerComponent } from '@shared/components/language-picker/language-picker.component';
import { UrlParamProviderService } from '@shared/services/url-param-provider.service';
import { PrimeNG } from 'primeng/config';
import { BrokernetLanguageType, BrokernetLanguageTypeOptions } from './api/backend';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    TranslocoPipe,
    FormsModule,
    QuestionBoxComponent,
    LanguagePickerComponent,
    RouterLink
  ],
  providers: [
    CurrencyPipe,
    CallidusCurrencyPipe
  ],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss'
})
export class AppComponent extends unsubscribeMixin() implements OnInit {

  private primengConfig = inject(PrimeNG);
  private translocoService = inject(TranslocoService);
  private router = inject(Router);
  private urlParamProviderService = inject(UrlParamProviderService);

  insuranceTypeAnswerOptions: AnswerOption<ContractSelectOptionsEnum>[] = [
    { title: 'bvs.kasko', id: ContractSelectOptionsEnum.BVS_HAFTPFLICHT_UND_KASKO, value: ContractSelectOptionsEnum.BVS_HAFTPFLICHT_UND_KASKO },
    { title: 'bvs.haftpflicht', id: ContractSelectOptionsEnum.BVS_HAFTPFLICHT, value: ContractSelectOptionsEnum.BVS_HAFTPFLICHT },
    { title: 'contract.skipper', id: ContractSelectOptionsEnum.SKIPPER, value: ContractSelectOptionsEnum.SKIPPER },
    { title: 'contract.bvs.charterkautionsversicherung', id: ContractSelectOptionsEnum.CHARTER_KAUTION, value: ContractSelectOptionsEnum.CHARTER_KAUTION }
  ];

  languageOptions: AppLanguage[] = BrokernetLanguageTypeOptions.map(option => ({
    key: option.value,
    label: option.labelKey,
    flagIcon: this.getFlagForLangCode(option.value)
  }));
  selectedContractOption: ContractSelectOptionsEnum | null = null;

  selectedLanguage: AppLanguage = this.languageOptions.find(l => l.key === this.translocoService.getActiveLang())!;

  ngOnInit(): void {
    this.urlParamProviderService.storeParamsFromUrl();

    if (!localStorage.getItem('app-language')) {
      localStorage.setItem('app-language', BrokernetLanguageType.De);
    }

    this.router
      .events
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe((event) => {
        if (event instanceof NavigationEnd) {
          if (event.url === '/') {
            this.selectedContractOption = null;
          } else {
            this.selectedContractOption = this.getContractSelectOptionByRoute((event as NavigationEnd).url);
          }
        }

      });

    this.translocoService.load(BrokernetLanguageType.De)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(() => this.primengConfig.setTranslation(this.translocoService.getTranslation('primeng')));
  }

  setSelectedLanguage(language: AppLanguage): void {
    this.initLanguageSettings(language.key);
    this.selectedLanguage = language;
    localStorage.setItem('app-language', language.key);
  }

  routeToContractPage(contractSelectOption: ContractSelectOptionsEnum): void {
    this.selectedContractOption = contractSelectOption;

    let route = '';

    switch (contractSelectOption) {
      case ContractSelectOptionsEnum.BVS_HAFTPFLICHT:
        route = '/bvs/haftpflicht';
        break;

      case ContractSelectOptionsEnum.BVS_HAFTPFLICHT_UND_KASKO:
        route = '/bvs/kasko';
        break;

      case ContractSelectOptionsEnum.CHARTER_KAUTION:
        route = '/bvs/charter';
        break;

      case ContractSelectOptionsEnum.SKIPPER:
        route = '/skipper';
        break;
    }

    void this.router.navigate([route], { queryParams: this.urlParamProviderService.getQueryData() });
  }

  private initLanguageSettings(lang: BrokernetLanguageType): void {
    this.translocoService
      .load(lang)
      .pipe(take(1))
      .subscribe(() => this.primengConfig.setTranslation(this.translocoService.getTranslation('primeng')));

    this.translocoService.setActiveLang(lang);
  }

  private getFlagForLangCode(langCode: BrokernetLanguageType): string {
    if (langCode === BrokernetLanguageType.De) {
      return 'austria';
    }

    return 'united-kingdom';
  }

  private getContractSelectOptionByRoute(route: string): ContractSelectOptionsEnum | null {
    // ignore parameters like customerId
    const cleanRoute = route.split('?')[0];

    switch (cleanRoute) {
      case '/bvs/haftpflicht':
        return ContractSelectOptionsEnum.BVS_HAFTPFLICHT;
      case '/bvs/kasko':
        return ContractSelectOptionsEnum.BVS_HAFTPFLICHT_UND_KASKO;
      case '/bvs/charter':
        return ContractSelectOptionsEnum.CHARTER_KAUTION;
      case '/skipper':
        return ContractSelectOptionsEnum.SKIPPER;
      default:
        return null;
    }
  }

}

enum ContractSelectOptionsEnum {
  BVS_HAFTPFLICHT_UND_KASKO = 'BVS_HAFTPFLICHT_UND_KASKO',
  BVS_HAFTPFLICHT = 'BVS_HAFTPFLICHT',
  SKIPPER = 'SKIPPER',
  CHARTER_KAUTION = 'CHARTER_KAUTION'
}

